import { useState, useEffect } from 'react';
import { 
  Search, Filter, Eye, Loader2, ChevronLeft, ChevronRight,
  Package, Truck, CheckCircle, XCircle, Clock, FileText,
  MapPin, Phone, Mail, Calendar, CreditCard, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/shop';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  packed: { label: 'Packed', color: 'bg-indigo-100 text-indigo-700', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  refunded: { label: 'Refunded', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

export default function AdminShopOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Detail modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingId, setTrackingId] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [page, search, statusFilter]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_admin_token')}` }
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/orders?${params}`, getAuthHeaders());
      setOrders(response.data.orders);
      setStats(response.data.stats);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const openOrderDetail = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, getAuthHeaders());
      setSelectedOrder(response.data);
      setNewStatus(response.data.status);
      setTrackingId(response.data.tracking_id || '');
      setStatusNotes('');
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || newStatus === selectedOrder.status) return;
    
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/status`, {
        status: newStatus,
        tracking_id: trackingId || null,
        notes: statusNotes || null
      }, getAuthHeaders());
      
      toast.success(`Order status updated to ${statusConfig[newStatus].label}`);
      setShowDetailModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`, getAuthHeaders());
      // For now, we'll just show the data - in production this would generate a PDF
      console.log('Invoice data:', response.data);
      toast.info('Invoice generation coming soon!');
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Orders</h1>
        <p className="text-slate-500 text-sm mt-1">Manage customer orders</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { key: 'pending', label: 'Pending', color: 'bg-yellow-500' },
          { key: 'confirmed', label: 'Confirmed', color: 'bg-blue-500' },
          { key: 'shipped', label: 'Shipped', color: 'bg-purple-500' },
          { key: 'delivered', label: 'Delivered', color: 'bg-green-500' },
          { key: 'cancelled', label: 'Cancelled', color: 'bg-red-500' },
        ].map(item => (
          <div key={item.key} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center mb-3`}>
              <span className="text-white font-bold">{stats[item.key] || 0}</span>
            </div>
            <p className="text-sm text-slate-600">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by Order ID, email, name..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            <option value="">All Status</option>
            {Object.entries(statusConfig).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Order</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Items</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((order) => {
                  const status = statusConfig[order.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  
                  return (
                    <tr key={order.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-mono text-sm font-medium text-slate-900">
                          #{order.order_number || order.id.slice(0, 8).toUpperCase()}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{order.user_name || 'N/A'}</p>
                        <p className="text-xs text-slate-500">{order.user_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{order.items?.length || 0} items</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-slate-900">₹{order.total?.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-500">{formatDate(order.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openOrderDetail(order.id)}
                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => downloadInvoice(order.id)}
                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                            title="Download Invoice"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Order #{selectedOrder.order_number || selectedOrder.id.slice(0, 8).toUpperCase()}
                </h2>
                <p className="text-sm text-slate-500">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Customer Details</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <span className="text-slate-500">Name:</span>
                      <span className="font-medium">{selectedOrder.user_name || 'N/A'}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{selectedOrder.user_email}</span>
                    </p>
                    {selectedOrder.user_phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedOrder.user_phone}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Shipping Address</h3>
                  {selectedOrder.shipping_address ? (
                    <div className="text-sm space-y-1">
                      <p className="font-medium">{selectedOrder.shipping_address.name}</p>
                      <p className="text-slate-600">{selectedOrder.shipping_address.address}</p>
                      <p className="text-slate-600">
                        {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} - {selectedOrder.shipping_address.pincode}
                      </p>
                      {selectedOrder.shipping_address.phone && (
                        <p className="text-slate-600">Ph: {selectedOrder.shipping_address.phone}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No shipping address</p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Order Items</h3>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Product</th>
                        <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Qty</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Price</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-100 rounded-lg overflow-hidden">
                                {item.product_image ? (
                                  <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-4 h-4 text-slate-300" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{item.product_name}</p>
                                <p className="text-xs text-slate-500">SKU: {item.product_sku || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">{item.quantity}</td>
                          <td className="px-4 py-3 text-right">₹{item.price?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-medium">₹{(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Order Summary */}
                <div className="mt-4 flex justify-end">
                  <div className="w-64 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Subtotal</span>
                      <span>₹{selectedOrder.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Shipping</span>
                      <span>{selectedOrder.shipping_charge === 0 ? 'FREE' : `₹${selectedOrder.shipping_charge?.toFixed(2)}`}</span>
                    </div>
                    {selectedOrder.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>-₹{selectedOrder.discount?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t border-slate-200 pt-2">
                      <span>Total</span>
                      <span>₹{selectedOrder.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <CreditCard className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Payment Method</p>
                  <p className="font-medium">{selectedOrder.payment_method || 'N/A'}</p>
                </div>
                <div className="ml-auto">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedOrder.payment_status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </div>
              </div>

              {/* Status Update */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Update Status</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">New Status</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                    >
                      {Object.entries(statusConfig).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Tracking ID</label>
                    <input
                      type="text"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      placeholder="Enter tracking number"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm text-slate-600 mb-1">Notes (optional)</label>
                  <textarea
                    value={statusNotes}
                    onChange={(e) => setStatusNotes(e.target.value)}
                    rows={2}
                    placeholder="Add notes for this status update..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  />
                </div>
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleUpdateStatus}
                    disabled={updatingStatus || newStatus === selectedOrder.status}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                  >
                    {updatingStatus && <Loader2 className="w-4 h-4 animate-spin" />}
                    Update Status
                  </button>
                </div>
              </div>

              {/* Status History */}
              {selectedOrder.status_history?.length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Status History</h3>
                  <div className="space-y-3">
                    {selectedOrder.status_history.map((history, idx) => {
                      const historyStatus = statusConfig[history.status] || statusConfig.pending;
                      return (
                        <div key={idx} className="flex items-start gap-3 text-sm">
                          <div className={`w-2 h-2 mt-1.5 rounded-full ${historyStatus.color.split(' ')[0].replace('100', '500')}`} />
                          <div>
                            <p className="font-medium">{historyStatus.label}</p>
                            <p className="text-slate-500">{formatDate(history.updated_at)}</p>
                            {history.notes && <p className="text-slate-600 mt-1">{history.notes}</p>}
                            <p className="text-xs text-slate-400">by {history.updated_by}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
